/**
 * PrepTestModal - Beredskapstestet som modal
 * Kan köras från Dashboard eller Inställningar
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft, 
  X,
  Target,
  Sparkles,
} from 'lucide-react';
import { usePrepTestStore } from '@/stores/prep-test-store';
import { areaConfig } from '@/config/area-config';

interface QuizQuestion {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  question: string;
  options: { value: number; label: string }[];
  area: string;
  color: string;
}

// Beredskapsfrågor baserade på Hemberedskapsskolan
const quizQuestions: QuizQuestion[] = [
  {
    id: 'water',
    icon: areaConfig.water.icon,
    question: 'Hur mycket dricksvatten har du lagrat hemma?',
    area: 'Vatten',
    color: areaConfig.water.color,
    options: [
      { value: 0, label: 'Inget alls' },
      { value: 25, label: 'Några flaskor (1-2 dagar)' },
      { value: 50, label: 'Dunkar för 3-4 dagar' },
      { value: 75, label: 'En veckas förråd (35L per person)' },
      { value: 100, label: 'Mer än en vecka + vattenrening' },
    ],
  },
  {
    id: 'food',
    icon: areaConfig.food.icon,
    question: 'Hur länge kan du klara dig med maten du har hemma?',
    area: 'Mat',
    color: areaConfig.food.color,
    options: [
      { value: 0, label: 'Mindre än 3 dagar' },
      { value: 25, label: '3-5 dagar' },
      { value: 50, label: 'Ungefär en vecka' },
      { value: 75, label: '2 veckor' },
      { value: 100, label: 'En månad eller mer' },
    ],
  },
  {
    id: 'cooking',
    icon: areaConfig.food.icon,
    question: 'Kan du laga varm mat utan el?',
    area: 'Mat',
    color: areaConfig.food.color,
    options: [
      { value: 0, label: 'Nej, ingen möjlighet' },
      { value: 50, label: 'Ja, jag har gasolkök eller stormkök' },
      { value: 100, label: 'Ja, och jag har övat på det' },
    ],
  },
  {
    id: 'light',
    icon: areaConfig.energy.icon,
    question: 'Har du alternativ belysning vid strömavbrott?',
    area: 'Energi',
    color: areaConfig.energy.color,
    options: [
      { value: 0, label: 'Nej, bara mobillampan' },
      { value: 25, label: 'Stearinljus' },
      { value: 50, label: 'Ficklampa med extra batterier' },
      { value: 75, label: 'Pannlampa + ficklampa + batterier' },
      { value: 100, label: 'Flera lampor + solcellsladdare' },
    ],
  },
  {
    id: 'power',
    icon: areaConfig.energy.icon,
    question: 'Kan du ladda mobilen vid långvarigt strömavbrott?',
    area: 'Energi',
    color: areaConfig.energy.color,
    options: [
      { value: 0, label: 'Nej' },
      { value: 25, label: 'Powerbank för ett par laddningar' },
      { value: 50, label: 'Stor powerbank eller billaddare' },
      { value: 100, label: 'Solcellsladdare eller större batteribank' },
    ],
  },
  {
    id: 'firstaid',
    icon: areaConfig.health.icon,
    question: 'Hur är din första hjälpen-utrustning?',
    area: 'Hälsa',
    color: areaConfig.health.color,
    options: [
      { value: 0, label: 'Har ingen' },
      { value: 25, label: 'Några plåster' },
      { value: 50, label: 'Grundläggande förbandsväska' },
      { value: 75, label: 'Komplett första hjälpen-väska' },
      { value: 100, label: 'Komplett + tourniquet + extra mediciner' },
    ],
  },
  {
    id: 'knowledge',
    icon: areaConfig.health.icon,
    question: 'Kan du grundläggande första hjälpen (HLR, stoppa blödning)?',
    area: 'Hälsa',
    color: areaConfig.health.color,
    options: [
      { value: 0, label: 'Nej, aldrig lärt mig' },
      { value: 25, label: 'Gick kurs för länge sedan' },
      { value: 50, label: 'Har grundläggande kunskaper' },
      { value: 100, label: 'Ja, och håller kunskaperna färska' },
    ],
  },
  {
    id: 'radio',
    icon: areaConfig.communication.icon,
    question: 'Kan du ta emot nyheter och VMA utan internet?',
    area: 'Information',
    color: areaConfig.communication.color,
    options: [
      { value: 0, label: 'Nej, bara via mobilen' },
      { value: 50, label: 'Har en radio hemma någonstans' },
      { value: 75, label: 'Har batteridriven radio redo' },
      { value: 100, label: 'Vevradio eller solcellsradio' },
    ],
  },
  {
    id: 'warmth',
    icon: areaConfig.shelter.icon,
    question: 'Hur klarar du dig om värmen försvinner vintertid?',
    area: 'Skydd',
    color: areaConfig.shelter.color,
    options: [
      { value: 0, label: 'Ingen plan' },
      { value: 25, label: 'Har extra filtar' },
      { value: 50, label: 'Filtar + sovsäckar' },
      { value: 75, label: 'Reservvärme (gasol/etanolkamin)' },
      { value: 100, label: 'Braskamin eller komplett backup' },
    ],
  },
  {
    id: 'transport',
    icon: areaConfig.transport.icon,
    question: 'Har du en plan om du behöver evakuera?',
    area: 'Transport',
    color: areaConfig.transport.color,
    options: [
      { value: 0, label: 'Nej, ingen plan' },
      { value: 25, label: 'Vet vart jag skulle ta vägen' },
      { value: 50, label: 'Plan + tankat fordon' },
      { value: 75, label: 'Plan + packad evakueringsväska' },
      { value: 100, label: 'Komplett plan som familjen övat på' },
    ],
  },
];

type TestStep = 'intro' | 'test' | 'analyzing' | 'complete';

interface PrepTestModalProps {
  onClose: () => void;
  isFirstTime?: boolean;
}

export default function PrepTestModal({ onClose, isFirstTime = false }: PrepTestModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { saveTestResults } = usePrepTestStore();
  const [step, setStep] = useState<TestStep>(isFirstTime ? 'intro' : 'test');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [score, setScore] = useState(0);

  // Lås scroll på body när modalen är öppen
  useEffect(() => {
    // Spara original styles för både html och body
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    const originalHtmlOverflow = htmlElement.style.overflow;
    const originalBodyOverflow = bodyElement.style.overflow;
    const originalBodyPaddingRight = bodyElement.style.paddingRight;
    
    // Beräkna scrollbar-bredd för att undvika layout shift
    const scrollbarWidth = window.innerWidth - htmlElement.clientWidth;
    
    // Lås scroll på både html och body
    htmlElement.style.overflow = 'hidden';
    bodyElement.style.overflow = 'hidden';
    
    if (scrollbarWidth > 0) {
      bodyElement.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      // Återställ till original state
      htmlElement.style.overflow = originalHtmlOverflow;
      bodyElement.style.overflow = originalBodyOverflow;
      bodyElement.style.paddingRight = originalBodyPaddingRight;
    };
  }, []);

  const handleStartTest = () => {
    setStep('test');
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleAnswer = (value: number) => {
    const question = quizQuestions[currentQuestion];
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);

    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Beräkna slutpoäng
      setStep('analyzing');
      
      // Spara testresultat i store för personliga rekommendationer
      saveTestResults(newAnswers);
      
      setTimeout(() => {
        const totalScore = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
        const maxScore = quizQuestions.length * 100;
        const percentage = Math.round((totalScore / maxScore) * 100);
        setScore(percentage);
        
        setStep('complete');
      }, 1500);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const getScoreLevel = (score: number) => {
    if (score >= 80) return { level: 'Avancerad hemberedskap', color: 'text-green-400', bgColor: 'bg-green-500/20', message: 'Du har en imponerande beredskap!' };
    if (score >= 60) return { level: 'God hemberedskap', color: 'text-blue-400', bgColor: 'bg-blue-500/20', message: 'Du har en bra grund, men det finns utrymme för förbättring.' };
    if (score >= 40) return { level: 'Grundläggande hemberedskap', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', message: 'Du har kommit igång. Fortsätt bygga din beredskap steg för steg.' };
    if (score >= 20) return { level: 'Hemberedskap för nybörjare', color: 'text-orange-400', bgColor: 'bg-orange-500/20', message: 'Du är i gång. Nu är det läge att börja bygga upp beredskapen på riktigt.' };
    return { level: 'Hemberedskap under uppbyggnad', color: 'text-red-400', bgColor: 'bg-red-500/20', message: 'Ingen fara, alla börjar någonstans.' };
  };

  const currentQ = quizQuestions[currentQuestion];
  const Icon = currentQ?.icon;
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      <div className="w-full max-w-lg rounded-xl bg-card border border-border shadow-xl overflow-hidden my-auto">
        {/* Header med stäng-knapp */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            {t('prepTest.title')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-muted transition-colors"
            title={t('common.cancel')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Intro */}
        {step === 'intro' && (
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('prepTest.intro.title')}</h3>
              <p className="text-muted-foreground">
                {t('prepTest.intro.description')}
              </p>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium mb-2">{t('prepTest.intro.whatYouGet')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('prepTest.intro.benefit1')}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('prepTest.intro.benefit2')}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('prepTest.intro.benefit3')}
                </li>
              </ul>
            </div>

            <button
              onClick={handleStartTest}
              className="w-full py-3 rounded-lg flex items-center justify-center gap-2 btn-primary-custom"
            >
              {t('prepTest.startTest')}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Frågor */}
        {step === 'test' && currentQ && (
          <div className="p-6 space-y-6">
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('prepTest.question')} {currentQuestion + 1} / {quizQuestions.length}
                </span>
                <span className="text-primary font-medium">{currentQ.area}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Fråga */}
            <div className="text-center py-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4" style={{ color: currentQ.color }}>
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium">{currentQ.question}</h3>
            </div>

            {/* Svarsalternativ */}
            <div className="space-y-2">
              {currentQ.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option.value)}
                  className={`w-full p-4 rounded-lg border text-left transition-all hover:border-primary/50 hover:bg-primary/5 ${
                    answers[currentQ.id] === option.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              {currentQuestion > 0 && (
                <button
                  onClick={handlePrevQuestion}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('common.back')}
                </button>
              )}
              <button
                onClick={onClose}
                className="ml-auto px-4 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Analyserar */}
        {step === 'analyzing' && (
          <div className="p-6 text-center py-12">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 animate-pulse">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t('prepTest.analyzing.title')}</h3>
            <p className="text-muted-foreground">{t('prepTest.analyzing.description')}</p>
          </div>
        )}

        {/* Resultat */}
        {step === 'complete' && (
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div className={`mx-auto w-20 h-20 rounded-full ${getScoreLevel(score).bgColor} flex items-center justify-center mb-4`}>
                <span className={`text-3xl font-bold ${getScoreLevel(score).color}`}>{score}%</span>
              </div>
              <h3 className="text-xl font-semibold mb-1">
                {t('prepTest.result.level')}: <span className={getScoreLevel(score).color}>{getScoreLevel(score).level}</span>
              </h3>
              <p className="text-muted-foreground">{getScoreLevel(score).message}</p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium mb-2">{t('prepTest.result.nextSteps')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('prepTest.result.nextStepsDesc')}
              </p>
            </div>

            <button
              onClick={() => {
                onClose();
                // Signal att rekommendationsmodalen ska öppnas
                sessionStorage.setItem('recommendationsModalOpen', 'true');
                navigate('/app');
              }}
              className="w-full py-3 rounded-lg flex items-center justify-center gap-2 btn-primary-custom"
            >
              <CheckCircle className="h-5 w-5" />
              {t('prepTest.result.done')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
